import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Download, Loader2, Mail, ArrowRight, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to ensure download URLs are absolute (prepends backend origin if relative)
const resolveDownloadUrl = (url) => {
  if (!url) return null;
  return url.startsWith("http")
    ? url
    : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoDownloadStarted, setAutoDownloadStarted] = useState(false);
  const [downloadStatuses, setDownloadStatuses] = useState({});

  const transactionRef = searchParams.get("ref");

  useEffect(() => {
    if (transactionRef) {
      fetchTransaction();
      clearCart();
    } else {
      setLoading(false);
    }
  }, [transactionRef]);

  const fetchTransaction = async () => {
    try {
      // Confirm payment with backend (triggers webhook check if needed)
      try {
        await axios.post(`${API}/payment/confirm/${transactionRef}`);
      } catch (e) { /* webhook handles it too */ }
      const response = await axios.get(`${API}/payment/status/${transactionRef}`);
      setTransaction(response.data);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-download all purchased files once transaction loads as completed
  useEffect(() => {
    if (!transaction || transaction.status !== "completed" || autoDownloadStarted) return;
    if (!transaction.items || transaction.items.length === 0) return;
    setAutoDownloadStarted(true);

    const downloadAll = async () => {
      for (let i = 0; i < transaction.items.length; i++) {
        const item = transaction.items[i];
        setDownloadStatuses(prev => ({ ...prev, [item.product_id]: "downloading" }));
        try {
          const res = await axios.get(`${API}/download/${transactionRef}?product_id=${item.product_id}`);
          if (res.data.download_url) {
            const link = document.createElement("a");
            link.href = resolveDownloadUrl(res.data.download_url);
            link.download = res.data.filename || "download";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadStatuses(prev => ({ ...prev, [item.product_id]: "done" }));
          } else {
            setDownloadStatuses(prev => ({ ...prev, [item.product_id]: "manual" }));
          }
        } catch {
          setDownloadStatuses(prev => ({ ...prev, [item.product_id]: "manual" }));
        }
        // Small delay between downloads so the browser doesn't block them
        if (i < transaction.items.length - 1) await new Promise(r => setTimeout(r, 1500));
      }
    };
    downloadAll();
  }, [transaction, autoDownloadStarted, transactionRef]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8 px-8">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-success" />
            </div>

            <h1 className="font-heading text-3xl font-bold mb-2" data-testid="success-title">
              Payment Successful!
            </h1>
            <p className="text-muted-foreground mb-2">
              Thank you for your purchase. Your order has been confirmed.
            </p>
            {autoDownloadStarted && Object.values(downloadStatuses).some(s => s === "downloading") && (
              <p className="text-sm text-accent font-medium mb-6 flex items-center justify-center gap-2" data-testid="auto-download-status">
                <Loader2 className="w-4 h-4 animate-spin" />
                Your files are downloading automatically...
              </p>
            )}
            {autoDownloadStarted && Object.values(downloadStatuses).length > 0 && Object.values(downloadStatuses).every(s => s === "done") && (
              <p className="text-sm text-green-600 font-medium mb-6 flex items-center justify-center gap-2" data-testid="auto-download-complete">
                <CheckCircle className="w-4 h-4" />
                All files downloaded successfully!
              </p>
            )}
            {!autoDownloadStarted && <div className="mb-6" />}

            {/* Order Details */}
            {transaction && (
              <div className="bg-secondary/50 rounded-xl p-6 text-left mb-8">
                <div className="grid gap-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Order Number</span>
                    <span className="font-mono font-medium">{transaction.transaction_ref}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span>{transaction.customer_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items</span>
                    <span>{transaction.items?.length || 0} product(s)</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-4">
                    <span>Total Paid</span>
                    <span className="text-primary">{formatPrice(transaction.total_amount)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Products */}
            {transaction?.items && transaction.items.length > 0 && (
              <div className="mb-8">
                <h3 className="font-medium text-left mb-4">Your Purchases</h3>
                <div className="space-y-3">
                  {transaction.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white border rounded-lg">
                      <span className="font-medium">{item.title}</span>
                      {downloadStatuses[item.product_id] === "downloading" ? (
                        <Button variant="outline" size="sm" disabled data-testid={`download-${index}`}>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Downloading...
                        </Button>
                      ) : downloadStatuses[item.product_id] === "done" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await axios.get(
                                `${API}/download/${transactionRef}?product_id=${item.product_id}`
                              );
                              if (response.data.download_url) {
                                const link = document.createElement("a");
                                link.href = resolveDownloadUrl(response.data.download_url);
                                link.download = response.data.filename || "download";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            } catch (error) {
                              alert(error.response?.data?.detail || "Download failed");
                            }
                          }}
                          data-testid={`download-${index}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                          Re-download
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await axios.get(
                                `${API}/download/${transactionRef}?product_id=${item.product_id}`
                              );
                              if (response.data.download_url) {
                                const link = document.createElement("a");
                                link.href = resolveDownloadUrl(response.data.download_url);
                                link.download = response.data.filename || "download";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            } catch (error) {
                              alert(error.response?.data?.detail || "Download failed");
                            }
                          }}
                          data-testid={`download-${index}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email Notice */}
            <div className="flex items-start gap-3 p-4 bg-accent/10 rounded-xl text-left mb-8">
              <Mail className="w-5 h-5 text-accent shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Check your email</p>
                <p className="text-sm text-muted-foreground">
                  We've sent a confirmation email with download links to {transaction?.customer_email}. 
                  Links are valid for 72 hours with up to 5 download attempts.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-primary">
                <Link to="/dashboard">
                  <User className="w-4 h-4 mr-2" />
                  View My Purchases
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/mock-exams">
                  Continue Shopping
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
